import FullStoryModule from '@modules/FullStoryModule';
import Task from '@modules/TasksModule/Task';

const MAX_ABANDONED_RING_DURATION_SEC = 5;
const MAX_ABANDONED_RING_DURATION_MS = MAX_ABANDONED_RING_DURATION_SEC * 1000; // 5 seconds

/**
 * Tracks missed/abandoned call events in FullStory for analytics purposes.
 *
 * This function logs two specific scenarios of missed calls:
 * 1. When a user was actively attempting to accept the call (isAnswering=true) but the call was abandoned
 * 2. When a call was not answered and abandoned within 5 seconds
 *
 * The function will not track events if:
 * - The task or sessionSid is null/undefined
 * - The call duration exceeds 5 seconds and the user wasn't attempting to answer
 *
 * **Error Handling:**
 * This function does not throw exceptions. All errors are caught internally and logged to console.error,
 * ensuring the function fails gracefully without disrupting the application flow.
 *
 * @param params - Object containing tracking parameters
 * @param params.task - The task object representing the call. Can be null/undefined for early return.
 * @param params.sessionSid - The unique session identifier for the call. Can be null/undefined for early return.
 * @param params.source - The source/context of where the missed call tracking is being triggered from.
 *                        Examples: 'rejected', 'abandoned', 'call-to-timeout'
 * @param params.eventType - Optional event type identifier for additional context
 *
 * @returns void - This function does not return a value
 *
 * @example
 * ```typescript
 * fullstoryMissedCallTracking({
 *   task: currentTask,
 *   sessionSid: 'session-123',
 *   source: 'abandoned',
 *   eventType: 'call.abandoned'
 * });
 * ```
 *
 * @see {@link Task} for task object structure
 * @see {@link FullStoryModule.trackEvent} for event tracking implementation
 */
const fullstoryMissedCallTracking = ({
  task,
  sessionSid,
  source,
  eventType,
  queueTimeoutOccurred,
}: {
  task: Task | null | undefined;
  sessionSid: string | null | undefined;
  source: string | undefined;
  eventType?: string;
  queueTimeoutOccurred?: boolean;
}): void => {
  if (!task || !sessionSid) {
    return;
  }
  try {
    // Track call abandonment with ring duration
    const ringDurationMs = task.startedAt
      ? Date.now() - task.startedAt.getTime()
      : 0;

    // Build common event properties
    const eventProperties = {
      sessionSid,
      ringDurationMs,
      queueName: task.queueName,
      direction: task.direction,
      isInternal: task.isInternal,
      source,
      eventType,
      queueTimeoutOccurred,
    };

    // Check if user was attempting to accept the call when it went to abandoned
    if (task.attributes?.isAnswering) {
      console.warn('ccp-call: Call missed after accept', eventProperties);
      FullStoryModule.trackEvent(
        '[CCP-Call] Call missed after accept',
        eventProperties,
      );
    } else if (ringDurationMs <= MAX_ABANDONED_RING_DURATION_MS) {
      console.warn(
        `ccp-call: Call missed in under ${MAX_ABANDONED_RING_DURATION_SEC} seconds`,
        eventProperties,
      );
      FullStoryModule.trackEvent(
        `[CCP-Call] Call missed in under ${MAX_ABANDONED_RING_DURATION_SEC} seconds`,
        eventProperties,
      );
    }
  } catch (e) {
    console.error('fullstoryAbandonedCallTracking error', {
      sessionSid: sessionSid ?? 'unknown',
      taskId: task?.id ?? 'unknown',
      error: e,
    });
  }
};

export default fullstoryMissedCallTracking;
